import { Box, Typography } from "@mui/material";
import TlButton from "./TlButton";
import { motion } from "framer-motion";

/**
 * EmptyState — consistent empty list/table/page states
 *
 * icon           — Lucide/MUI icon element (shown large in soft-bg circle)
 * title          — heading (e.g. "No calls yet")
 * description    — supporting text
 * action         — { label, onClick, startIcon } primary CTA
 * secondaryAction— { label, onClick } secondary link-style CTA
 * size           — "sm" | "md" | "lg" (default "md")
 * inline         — lighter, neutral treatment for empty tables/lists inside cards
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = "md",
  inline = false,
  sx,
}) {
  const py       = size === "lg" ? 80 : size === "sm" ? 32 : 56;
  const iconSize = size === "lg" ? 64 : size === "sm" ? 40 : 52;
  const iconBg   = size === "lg" ? 96 : size === "sm" ? 60 : 80;

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: `${py}px`,
        px: 3,
        ...sx,
      }}
    >
      {icon && (
        <Box sx={{
          width:  iconBg,
          height: iconBg,
          borderRadius: "var(--r-2xl)",
          background: inline ? "var(--surface-2)" : "var(--c-cyan-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: size === "sm" ? 1.5 : 2.5,
          color: inline ? "var(--ink-400)" : "var(--c-cyan)",
          "& svg": { fontSize: iconSize },
        }}>
          {icon}
        </Box>
      )}

      <Typography sx={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: size === "sm" ? "var(--t-body)" : size === "lg" ? "var(--t-h3)" : "var(--t-h4)",
        color: "var(--ink-900)",
        letterSpacing: "-0.01em",
        mb: description ? "6px" : 0,
        lineHeight: 1.3,
      }}>
        {title}
      </Typography>

      {description && (
        <Typography sx={{
          fontSize: size === "sm" ? "var(--t-caption)" : "var(--t-body-sm)",
          color: "var(--ink-500)",
          maxWidth: 380,
          lineHeight: 1.6,
        }}>
          {description}
        </Typography>
      )}

      {(action || secondaryAction) && (
        <Box sx={{
          mt: size === "sm" ? 2 : 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}>
          {action && (
            <TlButton
              variant="primary"
              size={size === "sm" ? "sm" : "md"}
              onClick={action.onClick}
              startIcon={action.startIcon}
            >
              {action.label}
            </TlButton>
          )}
          {secondaryAction && (
            <TlButton
              variant="ghost"
              size="sm"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </TlButton>
          )}
        </Box>
      )}
    </Box>
  );
}
